import * as React from "react";

import { SelectionItem } from "@replo/design-system/blocks/selection/SelectionItem";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { toast } from "@replo/design-system/components/toast/Toast";
import {
  useCurrentProjectContext,
  useIsAgentEnabled,
} from "@v2/contexts/CurrentProjectContext";
import {
  clearFeatureFlagOverrides,
  disableFeatureFlag,
  enableFeatureFlag,
  isFeatureEnabled,
} from "@v2/features/infra/featureFlags";
import { projectRouter } from "@v2/trpc/routes/project";
import { routes } from "@v2/utils/router";
import { Search } from "lucide-react";
import { generatePath, matchPath, useLocation } from "react-router-dom";
import { featureFlags } from "replo-utils/lib/featureFlags";
import { useForceUpdate } from "replo-utils/react/use-force-update";

const AgentModeAction: React.FC = () => {
  const { project } = useCurrentProjectContext();
  const isEnabled = useIsAgentEnabled();
  const location = useLocation();
  const { mutateAsync: updateIsAgentEnabled, isPending } =
    projectRouter.updateIsAgentEnabled.useMutation();

  const handleToggle = async () => {
    if (!project?.id) {
      return;
    }
    const nextEnabled = !isEnabled;
    await updateIsAgentEnabled({
      projectId: project.id,
      enabled: nextEnabled,
    });

    const isOnChatRoute = Boolean(
      matchPath(`${routes.shops.chat}/*`, location.pathname) ||
        matchPath(`${routes.agent.chat}/*`, location.pathname),
    );
    let targetRoute: string;
    if (nextEnabled) {
      targetRoute = isOnChatRoute ? routes.agent.chat : routes.agent.home;
    } else {
      targetRoute = isOnChatRoute ? routes.shops.chat : routes.shops.home;
    }
    const targetPath = generatePath(targetRoute, { projectId: project.id });
    window.location.replace(targetPath);

    toast.success({
      title: isEnabled ? "Agent mode disabled" : "Agent mode enabled",
    });
  };

  if (!project) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface px-2.5 py-1.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-medium text-default">Agent Mode</span>
        <span className="text-xs text-muted">
          {isEnabled
            ? "Agent mode is enabled for this project. Do not disable this unless you are sure you want to revert to old V2 files."
            : "Enable agent mode. You will lose access to all files created on old V2."}
        </span>
      </div>
      <Button
        size="sm"
        variant={isEnabled ? "destructive" : "secondary"}
        className="shrink-0"
        loading={isPending}
        onClick={() => void handleToggle()}
      >
        {isEnabled ? "Disable" : "Enable"}
      </Button>
    </div>
  );
};

export const FeatureFlagsTab: React.FC = () => {
  /**
   * Note (Noah, 2023-01-19): We have to do this rerender hack because we
   * don't have a hook to respond to changes in posthog feature flags
   */
  const rerender = useForceUpdate();
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredSortedFlags = React.useMemo(() => {
    return featureFlags.filter((flag) => {
      return flag.value.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface px-2.5 py-1.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-xs font-medium text-default">
            Reset to Defaults
          </span>
          <span className="text-xs text-muted">
            Clear all local overrides and restore server-side flag values.{" "}
            <a
              className="underline underline-offset-2"
              href="https://www.notion.so/replo/Feature-Flags-Guide-for-Support-b360ef51974b4ea2bb68a1e38a48f9f9?pvs=4"
            >
              Learn more
            </a>
          </span>
        </div>
        <Button
          size="sm"
          variant="primary"
          className="shrink-0"
          onClick={() => {
            clearFeatureFlagOverrides();
            rerender();
          }}
        >
          Reset to Defaults
        </Button>
      </div>
      <AgentModeAction />
      <Input
        size="sm"
        placeholder="Search feature flags..."
        value={searchTerm}
        autoFocus
        onChange={(e) => setSearchTerm(e.target.value)}
        endEnhancer={<Search className="size-3.5 text-muted" />}
      />
      <div className="flex flex-col gap-1">
        {filteredSortedFlags.map((flag) => {
          const { value, description } = flag;
          return (
            <SelectionItem
              key={value}
              id={`feature-flag-${value}`}
              type="switch"
              variant="outline"
              label={value}
              description={description}
              checked={isFeatureEnabled(value)}
              UNSAFE_className="p-2 [&_[data-slot=field-label]]:text-xs [&_[data-slot=field-description]]:text-xs"
              onCheckedChange={() => {
                if (isFeatureEnabled(value)) {
                  disableFeatureFlag(value);
                } else {
                  enableFeatureFlag(value);
                }
                rerender();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
